import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
	MapPin,
	Calendar,
	Star,
	ArrowLeft,
	Wrench,
	Package,
	MessageSquare,
} from "lucide-react";
import { api } from "@/lib/api";
import type { User, Tool, Rating } from "@/lib/types";
import { TrustScore, TrustScoreBreakdown } from "./TrustScore";
import { ToolCard } from "./ToolCard";

interface ProfilePageProps {
	userId?: string;
	isOwnProfile?: boolean;
	onBack?: () => void;
	onViewTool: (toolId: string) => void;
}

export function ProfilePage({
	userId,
	isOwnProfile = true,
	onBack,
	onViewTool,
}: ProfilePageProps) {
	const [user, setUser] = useState<(User & { ratings?: Rating[]; trustScore?: number }) | null>(null);
	const [tools, setTools] = useState<(Tool & { owner?: User })[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadProfile();
	}, [userId]);

	async function loadProfile() {
		setLoading(true);
		try {
			let userData: User & { ratings?: Rating[]; trustScore?: number };
			if (userId) {
				userData = await api.getUser(userId);
			} else {
				const me = await api.getMe();
				userData = await api.getUser(me.id);
			}
			setUser(userData);

			const allTools = await api.getTools();
			setTools(allTools.filter((t) => t.ownerId === userData.id));
		} catch (e) {
			console.error("Failed to load profile:", e);
		} finally {
			setLoading(false);
		}
	}

	if (loading) {
		return (
			<div className="space-y-4">
				<div className="h-32 bg-muted rounded-lg animate-pulse" />
				<div className="h-48 bg-muted rounded-lg animate-pulse" />
			</div>
		);
	}

	if (!user) {
		return (
			<div className="text-center py-12">
				<p className="text-muted-foreground">User not found</p>
			</div>
		);
	}

	const memberSince = new Date(user.joinedAt).toLocaleDateString("en-US", {
		month: "long",
		year: "numeric",
	});

	const lenderRatings = (user.ratings || []).filter((r) => r.type === "lender");
	const borrowerRatings = (user.ratings || []).filter((r) => r.type === "borrower");

	return (
		<div className="space-y-6">
			{onBack && (
				<Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
					<ArrowLeft className="w-4 h-4" />
					Back
				</Button>
			)}

			{/* Profile header */}
			<Card>
				<CardContent className="p-6">
					<div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
						<Avatar className="h-20 w-20">
							<AvatarImage src={user.avatar} />
							<AvatarFallback className="text-xl">
								{user.name[0]}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1 text-center sm:text-left">
							<h1 className="text-xl font-bold">{user.name}</h1>
							<div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-1.5">
								<span className="text-sm text-muted-foreground flex items-center gap-1">
									<MapPin className="w-3.5 h-3.5" />
									{user.neighborhood}
								</span>
								<span className="text-sm text-muted-foreground flex items-center gap-1">
									<Calendar className="w-3.5 h-3.5" />
									Member since {memberSince}
								</span>
							</div>
							<p className="text-sm text-muted-foreground mt-2">
								{user.bio}
							</p>
							<div className="mt-3">
								<TrustScore score={user.trustScore || user.trustScore || 0} size="md" />
							</div>
						</div>
					</div>

					<div className="grid grid-cols-4 gap-4 mt-6 pt-4 border-t border-border">
						<div className="text-center">
							<p className="text-xl font-bold">{user.totalLends}</p>
							<p className="text-xs text-muted-foreground">Tools Lent</p>
						</div>
						<div className="text-center">
							<p className="text-xl font-bold">{user.totalBorrows}</p>
							<p className="text-xs text-muted-foreground">Tools Borrowed</p>
						</div>
						<div className="text-center">
							<p className="text-xl font-bold">{user.responseRate}%</p>
							<p className="text-xs text-muted-foreground">Response Rate</p>
						</div>
						<div className="text-center">
							<p className="text-xl font-bold">
								{(user.ratings || []).length}
							</p>
							<p className="text-xs text-muted-foreground">Reviews</p>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Trust Score Breakdown */}
			<Card>
				<CardContent className="p-6">
					<TrustScoreBreakdown
						score={user.trustScore || 0}
						totalLends={user.totalLends}
						totalBorrows={user.totalBorrows}
						responseRate={user.responseRate}
						ratingCount={(user.ratings || []).length}
					/>
				</CardContent>
			</Card>

			{/* User's tools */}
			{tools.length > 0 && (
				<div className="space-y-3">
					<h2 className="text-lg font-semibold flex items-center gap-2">
						<Wrench className="w-5 h-5" />
						{isOwnProfile ? "My Listed Tools" : `${user.name.split(" ")[0]}'s Tools`}
						<Badge variant="secondary">{tools.length}</Badge>
					</h2>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{tools.map((tool) => (
							<ToolCard
								key={tool.id}
								tool={tool}
								onClick={() => onViewTool(tool.id)}
							/>
						))}
					</div>
				</div>
			)}

			{/* Reviews */}
			{(user.ratings || []).length > 0 && (
				<div className="space-y-3">
					<h2 className="text-lg font-semibold flex items-center gap-2">
						<MessageSquare className="w-5 h-5" />
						Reviews
						<Badge variant="secondary">{(user.ratings || []).length}</Badge>
					</h2>
					<div className="space-y-3">
						{(user.ratings || []).slice(0, 10).map((rating) => (
							<Card key={rating.id}>
								<CardContent className="p-4">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-1">
											{Array.from({ length: 5 }).map((_, i) => (
												<Star
													key={i}
													className={`w-3.5 h-3.5 ${
														i < rating.score
															? "fill-yellow-400 text-yellow-400"
															: "text-muted-foreground/20"
													}`}
												/>
											))}
										</div>
										<Badge variant="outline" className="text-[10px]">
											as {rating.type}
										</Badge>
									</div>
									<p className="text-sm mt-2">{rating.comment}</p>
									<p className="text-xs text-muted-foreground mt-1">
										{new Date(rating.createdAt).toLocaleDateString()}
									</p>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
